import {
  applyThemeAppearance,
  DEFAULT_THEME_APPEARANCE,
  isAccentColor,
  resolveContrastPreset,
  resolveSurfacePreset,
  type ThemeAppearance,
} from "@salestrak/ui";
import React from "react";
import {
  useUpdatePreferences,
  useUserSettings,
} from "#app/lib/hooks/use-user-settings";

/**
 * The user's accent and appearance presets, applied to the document.
 *
 * Two things have to be true at once, which is why this owns state rather than
 * reading preferences straight through:
 *
 *  - **Hovering an option has to repaint before it is chosen.** The pickers
 *    preview into local state; only a real choice persists. Otherwise walking
 *    a list of presets would be one mutation per option passed over.
 *  - **A reload must not flash the shipped design.** Preferences arrive over
 *    Apollo, so the first paint would land before they do. The mirror below
 *    seeds state synchronously from localStorage, the same trick the library's
 *    ThemeProvider uses for light/dark.
 *
 * The server stays the source of truth: whenever preferences resolve, they
 * overwrite local state and re-arm the mirror.
 */

const STORAGE_KEY = "theme_appearance";

/**
 * Reads the mirror. Anything malformed — a hand-edited entry, a value from an
 * older shape, a browser refusing storage — resolves to the shipped design
 * rather than throwing, because this runs during the first render and a throw
 * here takes the whole app down before anything paints.
 */
function readMirror(): ThemeAppearance {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME_APPEARANCE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_THEME_APPEARANCE;
    }
    const { accent, surface, contrast } = parsed as Record<string, unknown>;
    // Narrowed through named locals: a guard applied to an inline expression
    // refines that expression, not the `unknown` the value came from.
    const accentName = typeof accent === "string" ? accent : null;
    return {
      accent: isAccentColor(accentName) ? accentName : null,
      surface: resolveSurfacePreset(
        typeof surface === "string" ? surface : null,
      ),
      contrast: resolveContrastPreset(
        typeof contrast === "string" ? contrast : null,
      ),
    };
  } catch {
    return DEFAULT_THEME_APPEARANCE;
  }
}

function writeMirror(appearance: ThemeAppearance): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    // Storage disabled or full. The preference still round-trips through the
    // API; the only thing lost is the pre-paint seed on the next reload.
  }
}

interface ThemeAppearanceContextValue {
  /** What the page is painting, hovered preview included. */
  appearance: ThemeAppearance;
  /**
   * What the user actually chose.
   *
   * Separate from `appearance` because a preview paints without being a
   * choice: anything that describes the user's settings — a reset affordance,
   * a "customized" badge — has to read this one, or hovering an option makes
   * the interface claim a change nobody made.
   */
  committed: ThemeAppearance;
  /** Applies immediately without persisting — for a hovered option. */
  preview: (next: ThemeAppearance) => void;
  /** Applies and persists — for a chosen option or a reset. */
  commit: (next: ThemeAppearance) => void;
}

const ThemeAppearanceContext =
  React.createContext<ThemeAppearanceContextValue | undefined>(undefined);

export function ThemeAppearanceProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { preferences } = useUserSettings();
  const updatePreferences = useUpdatePreferences();
  const [appearance, setAppearance] =
    React.useState<ThemeAppearance>(readMirror);
  const [committed, setCommitted] = React.useState<ThemeAppearance>(appearance);

  // Null until preferences load (and while logged out), which is exactly when
  // the mirror should keep standing.
  const stored = React.useMemo<ThemeAppearance | null>(
    () =>
      preferences
        ? {
            accent: isAccentColor(preferences.themeColor)
              ? preferences.themeColor
              : null,
            surface: resolveSurfacePreset(preferences.themeSurfacePreset),
            contrast: resolveContrastPreset(preferences.themeContrastPreset),
          }
        : null,
    [preferences],
  );

  React.useEffect(() => {
    if (!stored) return;
    setAppearance(stored);
    setCommitted(stored);
    writeMirror(stored);
  }, [stored]);

  // Layout, not passive: this writes the custom properties the whole page
  // paints from, so it has to land before the browser paints the commit that
  // produced it or every change shows one frame of the previous theme.
  React.useLayoutEffect(() => {
    applyThemeAppearance(appearance);
  }, [appearance]);

  const commit = React.useCallback(
    (next: ThemeAppearance) => {
      setAppearance(next);
      setCommitted(next);
      writeMirror(next);
      // ONE call, not three. Each write's optimistic response is built from the
      // cached preferences, so three in the same tick would each spread the
      // same pre-change snapshot and the last would revert the other two.
      //
      // Silent: these are cosmetic and land in bursts, and a "Preference
      // updated" toast per option chosen is noise. Errors still toast.
      void updatePreferences(
        {
          themeColor: next.accent,
          themeSurfacePreset: next.surface,
          themeContrastPreset: next.contrast,
        },
        { silent: true },
      );
    },
    [updatePreferences],
  );

  const value = React.useMemo<ThemeAppearanceContextValue>(
    () => ({ appearance, committed, preview: setAppearance, commit }),
    [appearance, committed, commit],
  );

  return (
    <ThemeAppearanceContext.Provider value={value}>
      {children}
    </ThemeAppearanceContext.Provider>
  );
}

export function useThemeAppearance(): ThemeAppearanceContextValue {
  const context = React.useContext(ThemeAppearanceContext);
  if (!context) {
    throw new Error(
      "useThemeAppearance must be used within a ThemeAppearanceProvider",
    );
  }
  return context;
}
